import {
  ArrowUpTrayIcon,
  DocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  type DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';

import { FileTtlSelect } from '@/components/FileTtlSelect';
import {
  type ProgressInfo,
  useUploadAndRegisterZipRepository,
} from '@/hooks/useFileRepository';
import { type FileTtl } from '@/hooks/useFiles';

const DEFAULT_MAX_SIZE_BYTES = 5 * 1024 ** 3; // 5 GB soft cap

function formatGB(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  // Whole-number GB → "5GB"; fractional → one decimal ("1.5GB").
  return Number.isInteger(gb) ? `${gb}GB` : `${gb.toFixed(1)}GB`;
}

type ItemStatus = 'uploading' | 'registering' | 'done' | 'error';

interface UploadItem {
  id: string;
  fileName: string;
  progress: number;
  oversize: boolean;
  status: ItemStatus;
  errorMessage: string | null;
  assetKey: string | null;
  controller: AbortController;
}

export interface ZipTargetUploaderProps {
  /**
   * Called with the current array of registered asset keys whenever an
   * item completes, errors, or is removed. The component owns the in-flight
   * + uploaded list internally; the parent only receives the registered
   * subset.
   */
  onChange: (assetKeys: string[]) => void;
  /**
   * Optional label shown above the drop zone (typically `'Repository'`).
   */
  label?: string;
  /**
   * Soft size cap in bytes. Files above this still upload, but a per-item
   * warning is shown. Defaults to 5 GB.
   */
  maxSizeBytes?: number;
  /**
   * Additional class names applied to the wrapper element.
   */
  className?: string;
}

function isProbablyZip(file: File): boolean {
  const lowerName = file.name.toLowerCase();
  return (
    lowerName.endsWith('.zip') ||
    file.type === 'application/zip' ||
    file.type === 'application/x-zip-compressed'
  );
}

function registeredKeys(items: UploadItem[]): string[] {
  return items
    .filter(i => i.status === 'done' && i.assetKey)
    .map(i => i.assetKey as string);
}

/**
 * `ZipTargetUploader` — drop-zone uploader that turns one or more local zip
 * files into file-type Repository assets. Each dropped file becomes its own
 * Repository target; the parent receives the full registered-key array via
 * `onChange`. Used inside `TargetSelector` when the user picks the "Upload
 * Zip" repository mode.
 *
 * Per-item state machine:
 *   - uploading    → row shows progress bar
 *   - registering  → row shows progress 100% + "Registering..."
 *   - done         → row shows filename chip
 *   - error        → row shows red error text + remove button
 *
 * The drop zone is always visible so users can append more files at any
 * time. Uploads run in parallel via independent `mutateAsync` calls; the
 * shared hook's exposed status fields would conflate concurrent uploads,
 * so we ignore them and drive UI from per-item state.
 *
 * Retention: an `Auto-delete` picker (shared `FileTtlSelect`) applies to
 * every file dropped while it's set. Defaults to 30 days (CUS-1618 —
 * uploaded source is transient scan input) but "Keep forever" is allowed.
 */
export function ZipTargetUploader({
  onChange,
  label,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  className,
}: ZipTargetUploaderProps) {
  const maxSizeLabel = formatGB(maxSizeBytes);
  const helperText = `Drop one or more zips. Up to ${maxSizeLabel} each; larger files transfer in chunks.`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [items, setItems] = useState<UploadItem[]>([]);
  // Uploaded source defaults to auto-expiring after 30 days (CUS-1618);
  // the picker lets users extend, shorten, or keep forever.
  const [ttl, setTtl] = useState<FileTtl | undefined>('30d');

  const { mutateAsync } = useUploadAndRegisterZipRepository();

  // Mirror items into a ref so the unmount-time cleanup can abort
  // all in-flight controllers without retriggering the cleanup effect.
  const itemsRef = useRef<UploadItem[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    return () => {
      itemsRef.current.forEach(item => item.controller.abort());
    };
  }, []);

  // Stable onChange ref so we can call it from inside setState updaters
  // without forcing the propagation logic to re-create per render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const updateItem = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems(prev => {
      const updated = prev.map(i => (i.id === id ? { ...i, ...patch } : i));
      // Push registered keys upward whenever assetKey or status changes.
      if ('assetKey' in patch || 'status' in patch) {
        onChangeRef.current(registeredKeys(updated));
      }
      return updated;
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems(prev => {
      const target = prev.find(i => i.id === id);
      target?.controller.abort();
      const updated = prev.filter(i => i.id !== id);
      onChangeRef.current(registeredKeys(updated));
      return updated;
    });
  }, []);

  const startUpload = useCallback(
    async (file: File) => {
      const controller = new AbortController();
      const id = crypto.randomUUID();
      const item: UploadItem = {
        id,
        fileName: file.name,
        progress: 0,
        oversize: file.size > maxSizeBytes,
        status: 'uploading',
        errorMessage: null,
        assetKey: null,
        controller,
      };
      setItems(prev => [...prev, item]);

      try {
        const { assetKey } = await mutateAsync(
          file,
          (p: ProgressInfo) => {
            // Once progress hits 100, the upload finished and we're
            // waiting on register. Reflect that transition in the row.
            updateItem(id, {
              progress: p.percent,
              status: p.percent >= 100 ? 'registering' : 'uploading',
            });
          },
          controller.signal,
          ttl
        );
        if (controller.signal.aborted) return;
        updateItem(id, { status: 'done', assetKey, progress: 100 });
      } catch (err) {
        if (controller.signal.aborted) return;
        const message =
          err instanceof Error ? err.message : 'Failed to upload zip';
        updateItem(id, { status: 'error', errorMessage: message });
      }
    },
    [maxSizeBytes, mutateAsync, updateItem, ttl]
  );

  const handleFiles = useCallback(
    (files: FileList | File[] | null | undefined) => {
      if (!files) return;
      const arr = Array.from(files);
      if (arr.length === 0) return;
      let warnedNonZip = false;
      for (const file of arr) {
        if (!isProbablyZip(file) && !warnedNonZip) {
          toast.warning(
            "One of these doesn't look like a zip file. Continuing anyway."
          );
          warnedNonZip = true;
        }
        startUpload(file);
      }
    },
    [startUpload]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  return (
    <div
      data-testid="zip-target-uploader"
      className={twMerge('flex flex-col gap-2', className)}
    >
      {label && (
        <label className="text-chariot-text-primary text-sm font-medium">
          {label}
        </label>
      )}

      <button
        type="button"
        data-testid="zip-uploader-dropzone"
        onDragOver={e => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={twMerge(
          'border-chariot-stroke bg-chariot-background-primary flex flex-col items-center gap-1.5 rounded border border-dashed p-4 transition-colors',
          dragOver && 'border-chariot-primary bg-chariot-primary/5'
        )}
        aria-label={`Upload zip${label ? ` for ${label}` : ''}`}
      >
        <ArrowUpTrayIcon className="text-chariot-text-secondary size-5" />
        <span
          data-testid="zip-uploader-status"
          className="text-chariot-text-secondary text-sm"
        >
          Drop zips here or click to browse
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip"
        multiple
        data-testid="zip-uploader-input"
        className="hidden"
        onChange={e => {
          // Snapshot before resetting input.value — `e.target.files` is a
          // live FileList that empties when value is cleared, so reading
          // it after the reset would drop every selected file.
          const files = e.target.files ? Array.from(e.target.files) : [];
          // Reset value so the same file(s) can be re-selected (e.g.
          // after a removal).
          e.target.value = '';
          handleFiles(files);
        }}
      />

      <div className="flex items-center justify-between gap-2">
        <p className="text-chariot-text-secondary text-xs">{helperText}</p>
        <div
          className="flex shrink-0 items-center gap-2"
          data-testid="zip-uploader-ttl"
        >
          <span className="text-chariot-text-secondary text-xs font-semibold uppercase">
            Auto-delete
          </span>
          <FileTtlSelect value={ttl} onChange={setTtl} className="w-44" />
        </div>
      </div>

      {items.length > 0 && (
        <ul data-testid="zip-uploader-items" className="flex flex-col gap-1.5">
          {items.map(item => (
            <li
              key={item.id}
              data-testid="zip-uploader-item"
              data-item-status={item.status}
              className={twMerge(
                'border-chariot-stroke bg-chariot-background-primary flex flex-col gap-1 rounded border p-2',
                item.status === 'error' && 'border-red-500'
              )}
            >
              <div className="flex items-center gap-2">
                <DocumentIcon className="text-chariot-text-secondary size-5 shrink-0" />
                <span className="text-chariot-text-primary min-w-0 flex-1 truncate text-sm">
                  {item.fileName}
                </span>
                <span
                  data-testid="zip-uploader-item-status"
                  className="text-chariot-text-secondary shrink-0 text-xs"
                >
                  {item.status === 'uploading' &&
                    `Uploading ${Math.round(item.progress)}%`}
                  {item.status === 'registering' && 'Registering...'}
                  {item.status === 'done' && 'Ready'}
                  {item.status === 'error' && 'Failed'}
                </span>
                <button
                  type="button"
                  data-testid="zip-uploader-item-remove"
                  onClick={() => removeItem(item.id)}
                  className="text-chariot-text-secondary hover:text-chariot-text-primary shrink-0"
                  aria-label={`Remove ${item.fileName}`}
                >
                  <XMarkIcon className="size-4" />
                </button>
              </div>

              {(item.status === 'uploading' ||
                item.status === 'registering') && (
                <div
                  className="bg-chariot-stroke h-1.5 w-full overflow-hidden rounded"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(
                    item.status === 'registering' ? 100 : item.progress
                  )}
                  aria-label={`Upload progress for ${item.fileName}`}
                >
                  <div
                    data-testid="zip-uploader-item-progress"
                    className="bg-chariot-primary h-full transition-[width] duration-150"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(
                          0,
                          item.status === 'registering' ? 100 : item.progress
                        )
                      )}%`,
                    }}
                  />
                </div>
              )}

              {item.oversize && item.status !== 'error' && (
                <p className="text-xs text-amber-500">
                  Exceeds the {maxSizeLabel} soft cap. Upload will continue in
                  chunks but may be slow.
                </p>
              )}

              {item.status === 'error' && item.errorMessage && (
                <p
                  data-testid="zip-uploader-item-error"
                  className="text-xs text-red-500"
                >
                  {item.errorMessage}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
